// The customer's message a completed capture request posts (the server always
// writes one, with a default caption like "Here's a screenshot.", because the
// answer bot / AI reads it as the customer's turn). In the Messenger the
// request's card already shows what was sent, so that message folds into the
// card instead of repeating the screenshot right below it.

import { isVideoAttachment } from "../Components/MessageVideoPlayer/MessageVideoPlayer";
import { protectedFileId } from "../Services/AuthenticatedFiles";
import { decodeHtmlEntities } from "./HtmlEntities";

export interface CaptureResultAttachment {
  url: string;
  name?: string;
  type?: string;
}

export const isCaptureResultImage = (attachment?: CaptureResultAttachment) =>
  !!attachment?.url && /^image\//i.test(attachment.type ?? "");

// A page recording (rrweb JSON): nothing plays in the widget — the team
// replays it in the dashboard — so the card shows it as a recording tile.
export const isCapturePageRecording = (
  capture: { method?: string } | undefined,
  attachment?: CaptureResultAttachment,
) =>
  capture?.method === "rrweb" &&
  !!attachment?.url &&
  (attachment.type === "application/json" ||
    /\.json$/i.test(attachment.name ?? ""));

// What the card can show itself: a plain image, a playable video or a page
// recording's tile. Protected files keep their own message (it knows how to
// load them).
const isCardMedia = (
  capture: { method?: string } | undefined,
  attachment?: CaptureResultAttachment,
) =>
  !!attachment?.url &&
  !protectedFileId(attachment.url) &&
  (isCaptureResultImage(attachment) ||
    isVideoAttachment(attachment) ||
    isCapturePageRecording(capture, attachment));

// The captions the server writes when a capture arrives without text
// (Server capture.text.ts `defaultCaptureCaption`). Anything else is the
// customer's own words, which the card can't show.
const DEFAULT_CAPTURE_CAPTIONS = new Set(
  [
    "Here's a screenshot.",
    "Hier ist ein Screenshot.",
    "Voici une capture d'écran.",
    "Aquí tienes una captura de pantalla.",
    "Ecco uno screenshot.",
    "Hier is een screenshot.",
    "Aqui está uma captura de tela.",
    "Here are the screenshots.",
    "Hier sind die Screenshots.",
    "Voici les captures d'écran.",
    "Aquí tienes las capturas de pantalla.",
    "Ecco gli screenshot.",
    "Hier zijn de screenshots.",
    "Aqui estão as capturas de tela.",
    "Here's a screen recording.",
    "Hier ist eine Bildschirmaufnahme.",
    "Voici un enregistrement d'écran.",
    "Aquí tienes una grabación de pantalla.",
    "Ecco una registrazione dello schermo.",
    "Hier is een schermopname.",
    "Aqui está uma gravação de tela.",
  ].map((caption) => caption.toLowerCase()),
);

const textOf = (content: any): string => {
  if (typeof content === "string" || content instanceof String) {
    return decodeHtmlEntities(String(content).replace(/<[^>]*>/g, " "));
  }
  if (content && typeof content === "object") {
    const own = typeof content.text === "string" ? content.text : "";
    const children = Array.isArray(content.content)
      ? content.content.map(textOf).join(" ")
      : "";
    return `${own} ${children}`;
  }
  return "";
};

/** True when the message has no words of the customer's own (empty or the server's default caption). */
export const hasOnlyDefaultCaptureCaption = (content: any): boolean => {
  const text = textOf(content)
    .replace(/[\s\u00a0]+/g, " ")
    .trim()
    .toLowerCase();
  return !text || DEFAULT_CAPTURE_CAPTIONS.has(text);
};

/**
 * The customer's message for capture request `requestId`, when the card can
 * show its file (one image, video or page recording) and the message says
 * nothing else. Skips have no file and stay messages; so does a message with
 * the customer's own text next to the file (the card would drop the text).
 */
export const findCaptureResultComment = <
  T extends { data?: any; attachments?: any[] },
>(
  comments: T[] | null | undefined,
  requestId: string,
): T | undefined =>
  comments?.find((comment) => {
    const capture = comment?.data?.capture;
    return (
      capture?.requestId === requestId &&
      capture.skipped !== true &&
      comment.attachments?.length === 1 &&
      isCardMedia(capture, comment.attachments[0]) &&
      hasOnlyDefaultCaptureCaption(comment.data?.content)
    );
  });
